import React, { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import authService from '../../services/authService';
import { 
  hasFeatureAccess, 
  hasDashboardAccess, 
  getAccessibleQuickActions,
  filterNavigationByRole,
  shouldShowRoleIndicator,
  formatRoleName
} from '../../utils/roleBasedAccess';

/**
 * FixedSidebar Component
 * Fixed navigation sidebar for HR role-specific dashboards with role-based access control
 * Always visible on the left with a fixed width of 72 (w-72)
 */
const FixedSidebar = ({ currentPath }) => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);

  // Get current user and role on component mount
  useEffect(() => {
    const getCurrentUserRole = async () => {
      try {
        let user = authService.getStoredUser();
        if (!user) {
          user = await authService.getCurrentUser();
        }
        setCurrentUser(user);
        setUserRole(user?.role);
      } catch (error) {
        console.warn('[FixedSidebar] Could not get user role:', error);
        // Fallback to no role restrictions if auth fails
        setUserRole(null);
      }
    };

    getCurrentUserRole();
  }, []);

  // HR Role Navigation Items with role-based filtering
  const hrRoleItems = filterNavigationByRole([
    {
      label: 'Manager Dashboard',
      path: '/hr-roles/manager',
      icon: 'BarChart3',
      description: 'Strategic insights and team oversight',
      featureKey: 'manager-dashboard',
      requiredRoles: ['admin', 'hr_manager']
    },
    {
      label: 'Recruiter Dashboard',
      path: '/hr-roles/recruiter',
      icon: 'UserPlus',
      description: 'Daily recruitment activities',
      featureKey: 'recruiter-dashboard',
      requiredRoles: ['admin', 'hr_manager', 'recruiter']
    },
    {
      label: 'Coordinator Dashboard',
      path: '/hr-roles/coordinator',
      icon: 'Calendar',
      description: 'Administrative and scheduling coordination',
      featureKey: 'coordinator-dashboard',
      requiredRoles: ['admin', 'hr_manager', 'hr_coordinator', 'hr_user']
    }
  ], userRole);

  // Main System Navigation Items with role-based filtering
  const mainNavigationItems = filterNavigationByRole([
    {
      label: 'Main Dashboard',
      path: '/hr-dashboard',
      icon: 'Home',
      description: 'System overview and metrics',
      featureKey: 'dashboard'
    },
    {
      label: 'Candidate Database',
      path: '/candidate-database',
      icon: 'Users',
      description: 'Browse and manage candidates',
      featureKey: 'candidates'
    },
    {
      label: 'CV Processing',
      path: '/cv-upload-processing',
      icon: 'Upload',
      description: 'Upload and process CVs',
      featureKey: 'cv-processing'
    },
    {
      label: 'Interview Scheduling',
      path: '/interview-scheduling',
      icon: 'CalendarPlus',
      description: 'Schedule and manage interviews',
      featureKey: 'interviews'
    },
    {
      label: 'Job Management',
      path: '/job-management',
      icon: 'Briefcase',
      description: 'Create and manage job postings',
      featureKey: 'jobs',
      requiredRoles: ['admin', 'hr_manager', 'recruiter']
    },
    {
      label: 'User Management',
      path: '/user-management',
      icon: 'UserCog',
      description: 'Manage HR team members',
      featureKey: 'user-management',
      requiredRoles: ['admin', 'hr_manager']
    }
  ], userRole);

  // Quick Actions with role-based filtering
  const quickActionItems = filterNavigationByRole([
    {
      label: 'Top Candidates',
      path: '/candidate-database?filter=top-matches',
      icon: 'Star',
      description: 'AI-recommended matches',
      featureKey: 'candidates'
    },
    {
      label: "Today's Interviews",
      path: '/interview-scheduling?view=today',
      icon: 'Clock',
      description: 'View today\'s schedule',
      featureKey: 'interviews'
    }
  ], userRole);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const isActivePath = (path) => {
    return currentPath === path;
  };

  const renderNavItem = (item, isActive) => (
    <button
      key={item.path}
      onClick={() => handleNavigation(item.path)}
      className={`
        w-full flex items-center space-x-3 p-3 rounded-lg text-sm font-medium
        transition-all duration-200 micro-feedback group
        ${isActive
          ? 'bg-primary text-primary-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
        }
      `}
      title={item.description}
    >
      <Icon 
        name={item.icon} 
        size={18} 
        className={`flex-shrink-0 ${isActive ? '' : 'group-hover:text-foreground'}`}
      />
      <div className="flex-1 text-left">
        <div className="font-medium">{item.label}</div>
        <div className={`text-xs mt-0.5 ${
          isActive 
            ? 'text-primary-foreground/80' 
            : 'text-muted-foreground group-hover:text-muted-foreground'
        }`}>
          {item.description}
        </div>
      </div>
    </button>
  );

  // Don't render sidebar if user has no access to any navigation items
  const hasAnyAccess = hrRoleItems.length > 0 || mainNavigationItems.length > 0 || quickActionItems.length > 0;
  
  if (!hasAnyAccess) {
    return null;
  }

  return (
    <aside className="fixed left-0 top-16 bottom-0 w-72 bg-surface border-r border-border z-40">
      <div className="flex flex-col h-full">
        {/* Sidebar Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={18} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="font-semibold text-foreground text-sm">HR Roles</h2>
              <p className="text-xs text-muted-foreground">Role-based dashboards</p>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-4 space-y-6 overflow-y-auto">
          {/* HR Role Dashboards Section */}
          {hrRoleItems.length > 0 && (
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="Shield" size={14} className="text-primary" />
                <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  HR Role Dashboards
                </h3>
                {shouldShowRoleIndicator(userRole) && (
                  <span className="text-xs px-2 py-1 bg-primary/10 text-primary rounded-full">
                    {formatRoleName(userRole)}
                  </span>
                )}
              </div>
              <div className="space-y-2">
                {hrRoleItems.map((item) => 
                  renderNavItem(item, isActivePath(item.path))
                )}
              </div>
            </div>
          )}

          {/* System Navigation Section */}
          {mainNavigationItems.length > 0 && (
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="Settings" size={14} className="text-accent" />
                <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  System Navigation
                </h3>
              </div>
              <div className="space-y-2">
                {mainNavigationItems.map((item) => 
                  renderNavItem(item, isActivePath(item.path))
                )}
              </div>
            </div>
          )}

          {/* Quick Actions Section */}
          {quickActionItems.length > 0 && (
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <Icon name="Zap" size={14} className="text-warning" />
                <h3 className="text-xs font-semibold text-foreground uppercase tracking-wider">
                  Quick Actions
                </h3>
              </div>
              <div className="space-y-2">
                {quickActionItems.map((item) => 
                  renderNavItem(item, isActivePath(item.path))
                )}
              </div>
            </div>
          )}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-border">
          <div className="bg-muted/50 rounded-lg p-3">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Lightbulb" size={16} color="var(--color-accent)" />
              <span className="text-sm font-medium text-foreground">Role-Based Access</span>
            </div>
            <p className="text-xs text-muted-foreground">
              {userRole ? (
                `Showing features for ${userRole.replace('_', ' ')} role. Contact admin if you need access to additional features.`
              ) : (
                'Loading role-based navigation...'
              )}
            </p>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default FixedSidebar;